import { PerformanceTracker } from './types';

const INTERVAL_PERIOD = 100;
const EVENT_LISTENER_DELAY = 200;

/**
 * This resolves when loading indicator is no longer visible on the page.
 * It's used to mark the end of a performance measurement.
 *
 * Some notes about this implementation:
 *
 * - This function sets up an interval that checks for the presence of a loading indicator.
 * - When this is first invoked, the loading indicator may or may not already be visible.
 * - Whether it is expected to be visible is what the `waitForInitialExistence` parameter is for.
 * - If it is not initially visible, this function will wait for it to become visible.
 * - After it becomes visible, it will wait for it to disappear.
 * - After it disappears, it will wait for it to stay gone for a certain number of checks. (just to be extra sure)
 * - That certain number of checks is defined by the `disappearanceThreshold` parameter.
 * - Once it has been gone for that many checks, the promise will resolve with the time it
 *   first disappeared (firstDisappearance).
 * - If the user clicks anywhere on the page, the promise will reject and the measurement will be canceled.
 * - This is a best effort attempt to capture performance for a highly dynamic UI with many async loading indicators.
 */

export async function waitFor(
  log: PerformanceTracker['log'],
  selector: string,
  {
    disappearanceThreshold,
    waitForInitialExistence,
    signal
  }: {
    disappearanceThreshold: number;
    waitForInitialExistence: boolean;
    signal?: AbortSignal;
  }
): Promise<number> {
  const childLog = (...messages: unknown[]): void => {
    log(selector, ...messages);
  };
  let shouldExist = waitForInitialExistence;
  let timesMissingWhenChecked = 0;
  let firstDisappearance: number | undefined;

  return new Promise<number>((resolve, reject) => {
    const intervalId = setInterval(() => {
      const elements = document.querySelectorAll(selector);
      const elementExists = elements.length > 0;
      const missingLongEnough = timesMissingWhenChecked >= disappearanceThreshold;
      const missingBefore = timesMissingWhenChecked > 0;

      if (elementExists && shouldExist) {
        shouldExist = false;
        childLog('elements now exists');
      } else if (elementExists && !shouldExist && !missingBefore) {
        childLog('waiting for elements to not exist');
      } else if (elementExists && !shouldExist && missingBefore) {
        timesMissingWhenChecked = 0;
        firstDisappearance = undefined;
        childLog('found elements again');
      } else if (!elementExists && shouldExist) {
        childLog('waiting for elements to exist');
      } else if (!elementExists && !shouldExist && !missingLongEnough) {
        firstDisappearance = firstDisappearance ?? Date.now();
        timesMissingWhenChecked++;
        childLog('element no longer found, trying some more');
      } else if (!elementExists && !shouldExist && missingLongEnough) {
        cleanup();
        childLog('element has been gone long enough');
        resolve(firstDisappearance);
      } else {
        childLog('we should never get here');
        throw new Error(`Unexpected state: ${{ elementExists, shouldExist, missingLongEnough }}`);
      }
    }, INTERVAL_PERIOD);

    function handleAbort() {
      childLog('measurement was aborted');
      cleanup();
      reject(new Error('Measurement was aborted'));
    }

    function handleUserInterruption() {
      childLog('user clicked or navigated, canceling measurement');
      cleanup();
      reject(new Error('User interrupted measurement'));
    }

    function cleanup() {
      clearInterval(intervalId);
      document.removeEventListener('click', handleUserInterruption);
      window.removeEventListener('popstate', handleUserInterruption);
      window.removeEventListener('beforeunload', handleUserInterruption);
      signal?.removeEventListener('abort', handleAbort);
    }

    // Set up abort handling
    if (signal) {
      if (signal.aborted) {
        handleAbort();
        return;
      }
      signal.addEventListener('abort', handleAbort);
    }

    // Delay click and navigation interruption registration to avoid issue related to edit canceling itself.
    setTimeout(() => {
      childLog('measurement', 'setting up user interruption handlers');
      document.addEventListener('click', handleUserInterruption);
      window.addEventListener('popstate', handleUserInterruption);
      window.addEventListener('beforeunload', handleUserInterruption);
    }, EVENT_LISTENER_DELAY);
  });
}
